import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import { toast } from "sonner";

export default function Takeaway() {
  const { currentShopId } = useShop();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentShopId) return;

    try {
      const { data } = await api.get("/takeaway", {
        params: {
          shop_id: currentShopId,
        },
      });

      setOrders(data || []);
    } catch (error) {
      console.error(
        "Failed to load takeaway orders:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to load takeaway orders"
      );
    }
  }, [currentShopId]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh automatically every 8 seconds
  useEffect(() => {
    const id = setInterval(load, 8000);

    return () => clearInterval(id);
  }, [load]);

  const pending = orders.filter(
    (o) => o.status !== "delivered"
  );

  const done = orders.filter(
    (o) => o.status === "delivered"
  );

  // ==========================================================
  // MARK PAYMENT AS PAID
  // ==========================================================

  const markPaid = async (id) => {
    try {
      setLoading(true);

      await api.post(
        `/takeaway/${id}/mark-paid`
      );

      toast.success(
        "Payment marked as paid"
      );

      await load();
    } catch (error) {
      console.error(
        "Failed to mark payment:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to mark payment"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // DELIVER TAKEAWAY
  // ==========================================================

  const deliver = async (order) => {
    // Payment must be completed first.
    if (order.payment_status !== "paid") {
      toast.error(
        "Please collect payment before delivery"
      );
      return;
    }

    try {
      setLoading(true);

      await api.post(
        `/takeaway/${order.id}/deliver`
      );

      toast.success(
        `Order #${order.order_number} delivered`
      );

      await load();
    } catch (error) {
      console.error(
        "Failed to deliver takeaway order:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to deliver takeaway order"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="view"
      data-testid="takeaway-page"
    >
      {/* ======================================================
          PAGE HEADER
      ======================================================= */}

      <div className="page-head">
        <div>
          <div className="eyebrow">
            Counter · collection desk
          </div>

          <h1>Take Away</h1>

          <p>
            Collect payment, confirm handoff,
            keep the counter moving.
          </p>
        </div>
      </div>

      <div className="card">

        {/* ====================================================
            PENDING ORDERS
        ===================================================== */}

        <div className="subhead">
          Pending
        </div>

        <div className="table-wrap">
          <table data-testid="takeaway-pending-table">
            <tbody>

              <tr>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>

              {pending.map((o) => {
                const isPaid =
                  o.payment_status === "paid";

                return (
                  <tr
                    key={o.id}
                    data-testid={`takeaway-pending-${o.order_number}`}
                  >

                    {/* Customer */}
                    <td>
                      {o.customer_name}
                    </td>

                    {/* Items - each item on a new line */}
                    <td>
                      <div className="takeaway-items">
                        {o.lines?.map((l) => (
                          <div
                            key={l.item_id}
                            className="takeaway-item"
                          >
                            {l.name} ×{l.qty}
                          </div>
                        ))}
                      </div>
                    </td>

                    {/* Total */}
                    <td className="mono">
                      ₹{o.total}
                    </td>

                    {/* =================================================
                        PAYMENT
                    ================================================== */}

                    <td>
                      {isPaid ? (
                        <span className="status paid">
                          Paid
                        </span>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{
                            border:
                              "1px solid var(--border)",
                          }}
                          disabled={loading}
                          onClick={() =>
                            markPaid(o.id)
                          }
                          data-testid={`mark-paid-${o.order_number}`}
                        >
                          Pymnt Pending
                        </button>
                      )}
                    </td>

                    {/* =================================================
                        DELIVERY
                    ================================================== */}

                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={
                          !isPaid || loading
                        }
                        onClick={() =>
                          deliver(o)
                        }
                        data-testid={`takeaway-deliver-${o.order_number}`}
                      >
                        {isPaid
                          ? "Delivery"
                          : "Payment Required"}
                      </button>
                    </td>

                  </tr>
                );
              })}

            </tbody>
          </table>

          {pending.length === 0 && (
            <div
              className="empty"
              style={{
                margin: 16,
              }}
            >
              No pending takeaway orders
            </div>
          )}
        </div>

        {/* ====================================================
            DELIVERED ORDERS
        ===================================================== */}

        <div className="subhead">
          Delivered
        </div>

        <div className="table-wrap">
          <table data-testid="takeaway-done-table">
            <tbody>

              <tr>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>

              {done.map((o) => (
                <tr key={o.id}>

                  <td>
                    {o.customer_name}
                  </td>

                  {/* Items - each item on a new line */}
                  <td>
                    <div className="takeaway-items">
                      {o.lines?.map((l) => (
                        <div
                          key={l.item_id}
                          className="takeaway-item"
                        >
                          {l.name} ×{l.qty}
                        </div>
                      ))}
                    </div>
                  </td>

                  <td className="mono">
                    ₹{o.total}
                  </td>

                  <td>
                    <span
                      className={`status ${
                        o.payment_status
                      }`}
                    >
                      {o.payment_status}
                    </span>
                  </td>

                  <td>
                    <span className="status ready">
                      Delivered
                    </span>
                  </td>

                </tr>
              ))}

            </tbody>
          </table>

          {done.length === 0 && (
            <div
              className="empty"
              style={{
                margin: 16,
              }}
            >
              No delivered takeaway orders
            </div>
          )}
        </div>

      </div>
    </section>
  );
}